import React, { createContext, useContext, useState, useCallback } from 'react';

const LoadingContext = createContext();

export const useLoading = () => {
  const context = useContext(LoadingContext);
  if (!context) {
    throw new Error('useLoading must be used within a LoadingProvider');
  }
  return context;
};

export const LoadingProvider = ({ children }) => {
  const [activeRequests, setActiveRequests] = useState(new Set());
  const [timeoutIds, setTimeoutIds] = useState(new Map());

  const startLoading = useCallback((requestId) => {
    setActiveRequests(prev => new Set([...prev, requestId]));
    
    // Set 10-second timeout for this request
    const timeoutId = setTimeout(() => {
       stopLoading(requestId);
    }, 10000); // 10 seconds
    
    setTimeoutIds(prev => new Map([...prev, [requestId, timeoutId]]));
  }, []);

  const stopLoading = useCallback((requestId) => {
    setActiveRequests(prev => {
      const newSet = new Set(prev);
      newSet.delete(requestId);
      return newSet;
    });
    
    // Clear the timeout for this request
    setTimeoutIds(prev => {
      const newMap = new Map(prev);
      const timeoutId = newMap.get(requestId);
      if (timeoutId) {
        clearTimeout(timeoutId);
        newMap.delete(requestId);
      }
      return newMap;
    });
  }, []);

  const isLoading = activeRequests.size > 0;

  const value = {
    isLoading,
    startLoading,
    stopLoading,
    activeRequestsCount: activeRequests.size
  };

  return (
    <LoadingContext.Provider value={value}>
      {children}
    </LoadingContext.Provider>
  );
};
